/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import type { IconButtonProps } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { CoachChatLauncher } from 'bundles/ai-coach-platform/components/building-blocks';
import { ENTERPRISE_ADMIN_COACH_MODE_ID } from 'bundles/enterprise-admin-constants/EnterpriseCoachConstants';

const styles = {
  launcher: css`
    && [role='button'],
    && button {
      border-radius: var(--cds-border-radius-50);
      width: 36px;
      height: 36px;
    }
  `,
};

type Props = {
  onClick: () => void;
  defaultSelected?: boolean;
  tooltip?: string;
  size?: IconButtonProps['size'];
};

const EnterpriseCoachChatLauncher = ({ onClick, defaultSelected = false, tooltip, size = 'medium' }: Props) => {
  const track = useTracker();
  const { thirdPartySlug, programSlug } = useParams();

  const launcherVisibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_coach',
    {
      coachViewType: 'view_coach_tool_launcher',
      coach: {
        location: 'toolbar',
        mode: ENTERPRISE_ADMIN_COACH_MODE_ID,
      },
      enterprise: {
        organizationSlug: thirdPartySlug,
        programSlug,
      },
    },
    []
  );

  const handleClick = () => {
    track('interact_coach', {
      coachInteractionType: defaultSelected ? 'close_coach_tool' : 'launch_coach_tool',
      coach: {
        location: 'toolbar',
        mode: ENTERPRISE_ADMIN_COACH_MODE_ID,
      },
      enterprise: {
        organizationSlug: thirdPartySlug,
        programSlug,
      },
    });

    onClick();
  };

  return (
    <div ref={launcherVisibilityTrackingRef} css={styles.launcher} data-testid="enterprise-coach-chat-launcher">
      <CoachChatLauncher
        onClick={handleClick}
        defaultSelected={defaultSelected}
        tooltip={tooltip}
        size={size}
        enableBrandUpdate={true}
      />
    </div>
  );
};

export default EnterpriseCoachChatLauncher;
